import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { themeOrange } from "../../utils/colorTheme";
import { asyncStatus } from "../../utils/asyncStatus";
import {
  getUserTickets,
  createTicket,
  getTicketById,
  replyToTicket,
} from "../../services/supportTicketService";
import {
  resetCreateTicketStatus,
  resetReplyStatus,
  clearCurrentTicket,
} from "../../store/slices/support_ticket_slice";
import { IoAdd, IoClose, IoSend } from "react-icons/io5";
import { BsTicketPerforated } from "react-icons/bs";
import { format } from "date-fns";

const SupportTickets = () => {
  const dispatch = useDispatch();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [newTicket, setNewTicket] = useState({
    subject: "",
    description: "",
    category: "general",
    priority: "medium",
  });
  const [replyMessage, setReplyMessage] = useState("");

  const {
    user_tickets,
    user_tickets_status,
    create_ticket_status,
    current_ticket,
    current_ticket_status,
    reply_ticket_status,
  } = useSelector((state) => state.supportTickets);

  const isLoading = user_tickets_status === asyncStatus.LOADING;
  const isCreating = create_ticket_status === asyncStatus.LOADING;
  const isLoadingTicket = current_ticket_status === asyncStatus.LOADING;
  const isReplying = reply_ticket_status === asyncStatus.LOADING;

  useEffect(() => {
    dispatch(getUserTickets());
  }, [dispatch]);

  useEffect(() => {
    if (create_ticket_status === asyncStatus.SUCCEEDED) {
      setIsCreateModalOpen(false);
      setNewTicket({
        subject: "",
        description: "",
        category: "general",
        priority: "medium",
      });
      dispatch(resetCreateTicketStatus());
    }
  }, [create_ticket_status, dispatch]);

  useEffect(() => {
    if (reply_ticket_status === asyncStatus.SUCCEEDED) {
      setReplyMessage("");
      dispatch(resetReplyStatus());
    }
  }, [reply_ticket_status, dispatch]);

  const handleCreateTicket = () => {
    if (!newTicket.subject.trim() || !newTicket.description.trim()) return;
    dispatch(createTicket(newTicket));
  };

  const handleViewTicket = (ticketId) => {
    dispatch(getTicketById(ticketId));
    setIsViewModalOpen(true);
  };

  const handleCloseViewModal = () => {
    setIsViewModalOpen(false);
    dispatch(clearCurrentTicket());
  };

  const handleSendReply = () => {
    if (!replyMessage.trim() || !current_ticket) return;
    dispatch(replyToTicket({ ticketId: current_ticket.id, message: replyMessage }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "open":
        return "#2196f3";
      case "in_progress":
        return "#ff9800";
      case "resolved":
        return "#4caf50";
      case "closed":
        return "#9e9e9e";
      default:
        return "#9e9e9e";
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "low":
        return "#4caf50";
      case "medium":
        return "#ff9800";
      case "high":
        return "#f44336";
      case "urgent":
        return "#d32f2f";
      default:
        return "#9e9e9e";
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), "MMM dd, yyyy 'at' h:mm a");
    } catch {
      return dateString;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={4}
      >
        <Typography variant="h4" fontWeight="600" color="#1a1a1a">
          Support Tickets
        </Typography>
        <Button
          variant="contained"
          startIcon={<IoAdd />}
          onClick={() => setIsCreateModalOpen(true)}
          sx={{
            backgroundColor: themeOrange,
            "&:hover": { backgroundColor: "#d14e20" },
            borderRadius: "8px",
            textTransform: "none",
          }}
        >
          New Ticket
        </Button>
      </Stack>

      {isLoading ? (
        <Stack alignItems="center" justifyContent="center" minHeight="300px">
          <CircularProgress sx={{ color: themeOrange }} />
        </Stack>
      ) : user_tickets.length === 0 ? (
        <Card sx={{ p: 6, textAlign: "center", borderRadius: "16px" }}>
          <BsTicketPerforated size={60} color="#ccc" />
          <Typography variant="h6" color="text.secondary" mt={2}>
            No support tickets yet
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            Need help? Create a new ticket and our team will assist you.
          </Typography>
          <Button
            variant="contained"
            startIcon={<IoAdd />}
            onClick={() => setIsCreateModalOpen(true)}
            sx={{
              backgroundColor: themeOrange,
              "&:hover": { backgroundColor: "#d14e20" },
              borderRadius: "8px",
              textTransform: "none",
            }}
          >
            Create Your First Ticket
          </Button>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {user_tickets.map((ticket) => (
            <Grid item xs={12} md={6} key={ticket.id}>
              <Card
                sx={{
                  borderRadius: "12px",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  "&:hover": {
                    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                    transform: "translateY(-2px)",
                  },
                }}
                onClick={() => handleViewTicket(ticket.id)}
              >
                <CardContent>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    mb={2}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight="500"
                    >
                      #{ticket.ticket_number}
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Chip
                        label={ticket.priority}
                        size="small"
                        sx={{
                          backgroundColor: `${getPriorityColor(ticket.priority)}20`,
                          color: getPriorityColor(ticket.priority),
                          fontWeight: "600",
                          fontSize: "11px",
                          textTransform: "capitalize",
                        }}
                      />
                      <Chip
                        label={ticket.status.replace("_", " ")}
                        size="small"
                        sx={{
                          backgroundColor: `${getStatusColor(ticket.status)}20`,
                          color: getStatusColor(ticket.status),
                          fontWeight: "600",
                          fontSize: "11px",
                          textTransform: "capitalize",
                        }}
                      />
                    </Stack>
                  </Stack>
                  <Typography variant="subtitle1" fontWeight="600" mb={1}>
                    {ticket.subject}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    {ticket.description}
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(ticket.created_at)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {ticket.replies?.length || 0} replies
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create Ticket Modal */}
      <Dialog
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6" fontWeight="600">
              Create New Ticket
            </Typography>
            <IconButton onClick={() => setIsCreateModalOpen(false)}>
              <IoClose />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} mt={1}>
            <TextField
              label="Subject"
              fullWidth
              value={newTicket.subject}
              onChange={(e) =>
                setNewTicket({ ...newTicket, subject: e.target.value })
              }
              placeholder="Brief description of your issue"
            />
            <TextField
              label="Category"
              select
              fullWidth
              value={newTicket.category}
              onChange={(e) =>
                setNewTicket({ ...newTicket, category: e.target.value })
              }
              SelectProps={{ native: true }}
            >
              <option value="general">General Inquiry</option>
              <option value="billing">Billing</option>
              <option value="technical">Technical Issue</option>
              <option value="service">Service Related</option>
              <option value="complaint">Complaint</option>
              <option value="other">Other</option>
            </TextField>
            <TextField
              label="Priority"
              select
              fullWidth
              value={newTicket.priority}
              onChange={(e) =>
                setNewTicket({ ...newTicket, priority: e.target.value })
              }
              SelectProps={{ native: true }}
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </TextField>
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={4}
              value={newTicket.description}
              onChange={(e) =>
                setNewTicket({ ...newTicket, description: e.target.value })
              }
              placeholder="Please describe your issue in detail..."
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={() => setIsCreateModalOpen(false)}
            sx={{ color: "text.secondary" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateTicket}
            disabled={
              isCreating ||
              !newTicket.subject.trim() ||
              !newTicket.description.trim()
            }
            sx={{
              backgroundColor: themeOrange,
              "&:hover": { backgroundColor: "#d14e20" },
              borderRadius: "8px",
              textTransform: "none",
            }}
          >
            {isCreating ? (
              <CircularProgress size={20} sx={{ color: "white" }} />
            ) : (
              "Submit Ticket"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Ticket Modal */}
      <Dialog
        open={isViewModalOpen}
        onClose={handleCloseViewModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack>
              <Typography variant="h6" fontWeight="600">
                {current_ticket?.subject || "Loading..."}
              </Typography>
              {current_ticket && (
                <Typography variant="caption" color="text.secondary">
                  #{current_ticket.ticket_number}
                </Typography>
              )}
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              {current_ticket && (
                <>
                  <Chip
                    label={current_ticket.priority}
                    size="small"
                    sx={{
                      backgroundColor: `${getPriorityColor(current_ticket.priority)}20`,
                      color: getPriorityColor(current_ticket.priority),
                      fontWeight: "600",
                      textTransform: "capitalize",
                    }}
                  />
                  <Chip
                    label={current_ticket.status.replace("_", " ")}
                    size="small"
                    sx={{
                      backgroundColor: `${getStatusColor(current_ticket.status)}20`,
                      color: getStatusColor(current_ticket.status),
                      fontWeight: "600",
                      textTransform: "capitalize",
                    }}
                  />
                </>
              )}
              <IconButton onClick={handleCloseViewModal}>
                <IoClose />
              </IconButton>
            </Stack>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {isLoadingTicket ? (
            <Stack alignItems="center" py={4}>
              <CircularProgress sx={{ color: themeOrange }} />
            </Stack>
          ) : current_ticket ? (
            <Stack spacing={3}>
              {/* Original Message */}
              <Box
                sx={{
                  backgroundColor: "#f5f5f5",
                  borderRadius: "12px",
                  p: 2,
                }}
              >
                <Typography variant="body2" color="text.secondary" mb={1}>
                  {formatDate(current_ticket.created_at)}
                </Typography>
                <Typography variant="body1">
                  {current_ticket.description}
                </Typography>
              </Box>

              {/* Replies */}
              {current_ticket.replies?.length > 0 && (
                <Stack spacing={2}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Conversation
                  </Typography>
                  {current_ticket.replies.map((reply) => (
                    <Box
                      key={reply.id}
                      sx={{
                        backgroundColor: reply.is_admin ? "#fff3e0" : "#e3f2fd",
                        borderRadius: "12px",
                        p: 2,
                        ml: reply.is_admin ? 0 : 4,
                        mr: reply.is_admin ? 4 : 0,
                      }}
                    >
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        mb={1}
                      >
                        <Typography variant="caption" fontWeight="600">
                          {reply.is_admin ? "Support Team" : "You"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(reply.created_at)}
                        </Typography>
                      </Stack>
                      <Typography variant="body2">{reply.message}</Typography>
                    </Box>
                  ))}
                </Stack>
              )}

              {/* Reply Input */}
              {current_ticket.status !== "closed" && (
                <Stack direction="row" spacing={2} alignItems="flex-end">
                  <TextField
                    fullWidth
                    multiline
                    maxRows={4}
                    placeholder="Type your reply..."
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    disabled={isReplying}
                  />
                  <IconButton
                    onClick={handleSendReply}
                    disabled={isReplying || !replyMessage.trim()}
                    sx={{
                      backgroundColor: themeOrange,
                      color: "white",
                      "&:hover": { backgroundColor: "#d14e20" },
                      "&:disabled": { backgroundColor: "#ccc" },
                    }}
                  >
                    {isReplying ? (
                      <CircularProgress size={20} sx={{ color: "white" }} />
                    ) : (
                      <IoSend />
                    )}
                  </IconButton>
                </Stack>
              )}

              {current_ticket.status === "closed" && (
                <Box
                  sx={{
                    backgroundColor: "#f5f5f5",
                    borderRadius: "8px",
                    p: 2,
                    textAlign: "center",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    This ticket has been closed. If you need further assistance,
                    please create a new ticket.
                  </Typography>
                </Box>
              )}
            </Stack>
          ) : null}
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default SupportTickets;
